// API client for communicating with the backend

const API_BASE = (import.meta.env.VITE_API_BASE || '/api').trim().replace(/\/$/, '');

class APIClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const cleanEndpoint = endpoint.trim();
    const url = `${API_BASE}${cleanEndpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async getCurrentUser() {
    return this.request(`/me`);
  }

  // Courses
  async getCourses(params?: { status?: string; authorId?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/courses?${query}`);
  }

  async getCourse(id: string) {
    return this.request(`/courses/${id}`);
  }

  async createCourse(data: any) {
    return this.request(`/courses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCourse(id: string, data: any) {
    return this.request(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCourse(id: string) {
    return this.request(`/courses/${id}`, {
      method: 'DELETE',
    });
  }

  async publishCourse(id: string) {
    return this.request(`/courses/${id}/publish`, {
      method: 'POST',
    });
  }

  // Lessons
  async getLessons(courseId: string) {
    return this.request(`/lessons?courseId=${courseId}`);
  }

  async getLesson(id: string) {
    return this.request(`/lessons/${id}`);
  }

  async createLesson(data: any) {
    return this.request(`/lessons`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLesson(id: string, data: any) {
    return this.request(`/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLesson(id: string) {
    return this.request(`/lessons/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderLessons(lessonIds: string[]) {
    return this.request(`/lessons/reorder`, {
      method: 'POST',
      body: JSON.stringify({ lessonIds }),
    });
  }

  // Enrollments
  async getEnrollments(params?: { userId?: string; courseId?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/enrollments?${query}`);
  }

  async enrollUser(userId: string, courseId: string) {
    return this.request(`/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ userId, courseId }),
    });
  }

  async updateLessonProgress(
    enrollmentId: string,
    lessonId: string,
    data: any
  ) {
    return this.request(`/enrollments/progress/${enrollmentId}/${lessonId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Comments
  async getComments(params: { courseId: string; lessonId?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/comments?${query}`);
  }

  async createComment(data: any) {
    return this.request(`/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateComment(id: string, data: any) {
    return this.request(`/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Assets
  async uploadAsset(file: File, userId: string, tags?: string[]) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    if (tags) {
      formData.append('tags', JSON.stringify(tags));
    }

    const response = await fetch(`${API_BASE}/assets/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  // AI
  async generateCourseOutline(data: any) {
    return this.request(`/ai/generate-outline`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateQuiz(data: any) {
    return this.request(`/ai/generate-quiz`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async improveContent(content: string, instruction: string) {
    return this.request(`/ai/improve-content`, {
      method: 'POST',
      body: JSON.stringify({ content, instruction }),
    });
  }

  // Analytics
  async getCourseAnalytics(courseId: string) {
    return this.request(`/analytics/courses/${courseId}`);
  }

  async getUserAnalytics(userId: string) {
    return this.request(`/analytics/users/${userId}`);
  }

  async getDashboardAnalytics() {
    return this.request(`/analytics/dashboard`);
  }

  // User management
  async getUsers(params?: { role?: string }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/users?${query}`);
  }

  async updateUser(userId: string, data: any) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

export const api = new APIClient();
